import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-623b79ff"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "suggestion-header" }
const _hoisted_2 = { class: "suggestion-description" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = {
  key: 0,
  class: "suggestion-change"
}
const _hoisted_5 = {
  key: 1,
  class: "content-wrapper"
}
const _hoisted_6 = ["contenteditable", "onKeydown"]
const _hoisted_7 = ["title"]
const _hoisted_8 = { class: "suggestion-footer" }
const _hoisted_9 = { class: "suggestion-actions" }
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "bi bi-arrow-clockwise" }, null, -1))
const _hoisted_11 = [
  _hoisted_10
]

import { ref, nextTick, defineAsyncComponent } from "vue";
import { useSkillSuggestions } from "../../composables/useSkillSuggestions";
import { useSectionFormatting } from "../../composables/useSectionFormatting";
import {
  AddProjectAchievementMetadata,
  AddSummaryMetadata,
  AddWorkAchievementMetadata,
  AI_RESUME_COMMANDS,
  EditProjectAchievementMetadata,
  EditSummaryMetadata,
  EditWorkAchievementMetadata,
} from "../../../../server/src/models/dtos/ResumeSuggestionsOutput";
import { ResumeSuggestion } from "../../../../server/src/models/dtos/ResumeSuggestionsOutput";
import { Resume } from "@models/Resume";

// Import using defineAsyncComponent to handle the module without default export

export default /*#__PURE__*/_defineComponent({
  __name: 'SuggestionItem',
  props: {
    suggestion: {},
    resume: {}
  },
  emits: ["accept", "reject", "refresh", "highlight-text", "update:content"],
  setup(__props: any, { emit: __emit }) {

const SkillGroup = defineAsyncComponent(() => import("./SkillGroup.vue"));

const props = __props;

const emit = __emit;

// Local state
const isEditing = ref(false);

function getLocationText(suggestion: ResumeSuggestion) {
  if (suggestion.command === AI_RESUME_COMMANDS.ADD_WORK_ACHIEVEMENT) {
    const workExp = props.resume?.workExperience?.find((work) => work.uuid === suggestion.location);
    if (workExp) {
      return `${workExp.role} - ${workExp.company}`;
    }
  }
  if (
    suggestion.command === AI_RESUME_COMMANDS.EDIT_WORK_ACHIEVEMENT ||
    suggestion.command === AI_RESUME_COMMANDS.REMOVE_WORK_ACHIEVEMENT
  ) {
    const workExp = props.resume?.workExperience?.find((work) =>
      work.achievements?.some((achievement) => achievement.uuid === suggestion.location)
    );
    if (workExp) {
      return `${workExp.role} - ${workExp.company}`;
    }
  }
  if (
    suggestion.command === AI_RESUME_COMMANDS.EDIT_SUMMARY ||
    suggestion.command === AI_RESUME_COMMANDS.ADD_SUMMARY
  ) {
    return "Summary";
  }
  if (
    suggestion.command === AI_RESUME_COMMANDS.EDIT_PROJECT ||
    suggestion.command === AI_RESUME_COMMANDS.REMOVE_PROJECT
  ) {
    const project = props.resume?.projects?.find((project) =>
      project.achievements.some((p) => p.uuid === suggestion.location)
    );
    if (project) {
      return `${project.name}`;
    }
  }
  if (suggestion.command === AI_RESUME_COMMANDS.ADD_PROJECT) {
    const project = props.resume?.projects?.find((project) => project.uuid === suggestion.location);
    if (project) {
      return `${project.name}`;
    }
  }
  if (
    suggestion.command === AI_RESUME_COMMANDS.EDIT_SKILL_GROUP ||
    suggestion.command === AI_RESUME_COMMANDS.ADD_SKILL_GROUP ||
    suggestion.command === AI_RESUME_COMMANDS.REMOVE_SKILL
  ) {
    const skillGroup = props.resume?.additionalSkills?.find(
      (skillGroup) => skillGroup.uuid === suggestion.location
    );
    if (skillGroup) {
      return `${skillGroup.skillGroup}`;
    }
  }
  return "General";
}

function getSuggestionChange(suggestion: ResumeSuggestion) {
  if (
    suggestion.command === AI_RESUME_COMMANDS.EDIT_PROJECT ||
    suggestion.command === AI_RESUME_COMMANDS.ADD_PROJECT ||
    suggestion.command === AI_RESUME_COMMANDS.EDIT_WORK_ACHIEVEMENT ||
    suggestion.command === AI_RESUME_COMMANDS.ADD_WORK_ACHIEVEMENT
  ) {
    const metadata = suggestion.metadata as
      | AddProjectAchievementMetadata
      | AddWorkAchievementMetadata
      | EditProjectAchievementMetadata
      | EditWorkAchievementMetadata;
    return metadata?.achievement;
  }
  if (
    suggestion.command === AI_RESUME_COMMANDS.EDIT_SUMMARY ||
    suggestion.command === AI_RESUME_COMMANDS.ADD_SUMMARY
  ) {
    const metadata = suggestion.metadata as AddSummaryMetadata | EditSummaryMetadata;
    return metadata?.summary.description;
  }
  return suggestion.change;
}

function isDeleteSuggestion(suggestion: ResumeSuggestion): boolean {
  if (
    suggestion.command === AI_RESUME_COMMANDS.DELETE_SKILL_GROUP ||
    suggestion.command === AI_RESUME_COMMANDS.REMOVE_CERTIFICATION ||
    suggestion.command === AI_RESUME_COMMANDS.REMOVE_PROJECT ||
    suggestion.command === AI_RESUME_COMMANDS.REMOVE_WORK_ACHIEVEMENT ||
    suggestion.command === AI_RESUME_COMMANDS.REMOVE_SKILL ||
    suggestion.command === AI_RESUME_COMMANDS.REMOVE_EDUCATION
  ) {
    return true;
  }
  return false;
}
// Function to remove square brackets and their content from descriptions
function formatDescription(description: string): string {
  if (!description) return "";
  return description.replace(/\s*\[[^\]]*\]\s*/g, "");
}

// Import composables for skills and section formatting
const {
  isSkillSuggestion,
  getSkillGroupTitle,
  getSkillGroupSkills,
  updateSkills: handleUpdateSkills,
  updateTitle: handleUpdateTitle,
} = useSkillSuggestions();

const { formatSectionName, getSectionClass } = useSectionFormatting(props.resume);

// Handle skill group updates
function updateSkills(newSkills: string[]) {
  handleUpdateSkills(props.suggestion, newSkills);
}

function updateTitle(newTitle: string) {
  handleUpdateTitle(props.suggestion, newTitle);
}

// Handle mouse events for highlighting
function handleSuggestionMouseEnter() {
  if (
    props.suggestion.command === AI_RESUME_COMMANDS.EDIT_WORK_ACHIEVEMENT ||
    props.suggestion.command === AI_RESUME_COMMANDS.REMOVE_WORK_ACHIEVEMENT ||
    props.suggestion.command === AI_RESUME_COMMANDS.EDIT_SUMMARY ||
    props.suggestion.command === AI_RESUME_COMMANDS.EDIT_PROJECT ||
    props.suggestion.command === AI_RESUME_COMMANDS.ADD_PROJECT ||
    props.suggestion.command === AI_RESUME_COMMANDS.REMOVE_PROJECT ||
    props.suggestion.command === AI_RESUME_COMMANDS.EDIT_SKILL_GROUP ||
    props.suggestion.command === AI_RESUME_COMMANDS.DELETE_SKILL_GROUP
  ) {
    emit("highlight-text", {
      suggestion: props.suggestion,
      highlight: true,
    });
  }
}

function handleSuggestionMouseLeave() {
  if (
    props.suggestion.command === AI_RESUME_COMMANDS.EDIT_WORK_ACHIEVEMENT ||
    props.suggestion.command === AI_RESUME_COMMANDS.REMOVE_WORK_ACHIEVEMENT ||
    props.suggestion.command === AI_RESUME_COMMANDS.EDIT_SUMMARY ||
    props.suggestion.command === AI_RESUME_COMMANDS.EDIT_PROJECT ||
    props.suggestion.command === AI_RESUME_COMMANDS.ADD_PROJECT ||
    props.suggestion.command === AI_RESUME_COMMANDS.REMOVE_PROJECT ||
    props.suggestion.command === AI_RESUME_COMMANDS.EDIT_SKILL_GROUP ||
    props.suggestion.command === AI_RESUME_COMMANDS.ADD_SKILL_GROUP ||
    props.suggestion.command === AI_RESUME_COMMANDS.REMOVE_SKILL
  ) {
    emit("highlight-text", {
      suggestion: props.suggestion,
      highlight: false,
    });
  }
}

// Content editing functions
function handleContentEdit(event: Event) {
  const target = event.target as HTMLDivElement;
  const content = target.innerText;

  emit("update:content", {
    suggestion: props.suggestion,
    content,
  });
}

function handleContentBlur(event: Event) {
  const target = event.target as HTMLDivElement;
  const content = target.innerText.trim();

  emit("update:content", {
    suggestion: props.suggestion,
    content,
  });

  isEditing.value = false;
}

function handleContentEnter(event: KeyboardEvent) {
  event.preventDefault();
  const target = event.target as HTMLDivElement;
  const content = target.innerText;

  emit("update:content", {
    suggestion: props.suggestion,
    content,
  });

  (event.target as HTMLElement).blur();
}

function startEditing() {
  isEditing.value = true;
  nextTick(() => {
    const contentElement = document.querySelector('.content[contenteditable="true"]');
    if (contentElement) {
      contentElement.focus();

      // Place cursor at the end of content
      const range = document.createRange();
      const selection = window.getSelection();

      // Create a text node if there's no content
      if (!contentElement.firstChild) {
        contentElement.appendChild(document.createTextNode(""));
      }

      // Set selection to end of content
      const text = contentElement.firstChild as Text;
      const textLength = text.length;

      range.setStart(text, textLength);
      range.setEnd(text, textLength);

      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  });
}

function handleSaveContent() {
  isEditing.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["suggestion-item", {
      'add-suggestion': _ctx.suggestion.type === 'Add',
      'edit-suggestion': _ctx.suggestion.type === 'Edit',
      'remove-suggestion': _ctx.suggestion.type === 'Remove',
      'highlight-capable':
        _ctx.suggestion.command === _unref(AI_RESUME_COMMANDS).EDIT_WORK_ACHIEVEMENT ||
        _ctx.suggestion.command === _unref(AI_RESUME_COMMANDS).REMOVE_WORK_ACHIEVEMENT ||
        _ctx.suggestion.command === _unref(AI_RESUME_COMMANDS).EDIT_SUMMARY ||
        _ctx.suggestion.command === _unref(AI_RESUME_COMMANDS).EDIT_PROJECT ||
        _ctx.suggestion.command === _unref(AI_RESUME_COMMANDS).ADD_PROJECT ||
        _ctx.suggestion.command === _unref(AI_RESUME_COMMANDS).REMOVE_PROJECT,
    }]),
    onMouseenter: handleSuggestionMouseEnter,
    onMouseleave: handleSuggestionMouseLeave
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("p", _hoisted_2, _toDisplayString(formatDescription(_ctx.suggestion.description)), 1)
    ]),
    (!isDeleteSuggestion(_ctx.suggestion))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_ctx.suggestion.metadata)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (_unref(isSkillSuggestion)(_ctx.suggestion))
                  ? (_openBlock(), _createBlock(_unref(SkillGroup), {
                      key: 0,
                      title: _unref(getSkillGroupTitle)(_ctx.suggestion),
                      skills: _unref(getSkillGroupSkills)(_ctx.suggestion),
                      interactive: true,
                      isRemoval: _ctx.suggestion.type === 'Remove',
                      "onUpdate:skills": updateSkills,
                      "onUpdate:title": updateTitle
                    }, null, 8, ["title", "skills", "isRemoval"]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createElementVNode("div", {
                        class: "content",
                        contenteditable: isEditing.value,
                        onInput: handleContentEdit,
                        onBlur: handleContentBlur,
                        onKeydown: _withKeys(_withModifiers(handleContentEnter, ["prevent"]), ["enter"])
                      }, _toDisplayString(getSuggestionChange(_ctx.suggestion)), 41, _hoisted_6),
                      _createElementVNode("button", {
                        class: "edit-button",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (isEditing.value ? handleSaveContent() : startEditing())),
                        title: isEditing.value ? 'Save changes' : 'Edit suggestion'
                      }, [
                        _createElementVNode("i", {
                          class: _normalizeClass(isEditing.value ? 'bi bi-check' : 'bi bi-pencil')
                        }, null, 2)
                      ], 8, _hoisted_7)
                    ]))
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("span", {
        class: _normalizeClass(["section-badge", _unref(getSectionClass)(_ctx.suggestion.location)])
      }, _toDisplayString(getLocationText(_ctx.suggestion)), 3),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("button", {
          class: "btn-reject",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('reject', _ctx.suggestion))),
          title: "Reject"
        }, "✕"),
        _createElementVNode("button", {
          class: "btn-refresh",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('refresh', _ctx.suggestion))),
          title: "Regenerate suggestion"
        }, _hoisted_11),
        _createElementVNode("button", {
          class: "btn-accept",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('accept', _ctx.suggestion))),
          title: "Accept"
        }, "✓")
      ])
    ])
  ], 34))
}
}

})